import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { TCCPLoggerService } from './tccp-logger.service';
import * as _ from 'lodash';
import { LoaderService } from '../loader/loader.service';


/**
 * TCCP Global Http Response handler to catch any http error codes
 * and render appropriate error messages.
 */
@Injectable()
export class TCCPHttpResponseHandler {
    constructor(private router: Router, private logger: TCCPLoggerService, private loaderService: LoaderService) {
    }

    /**
     * Global http success handler
     * @param response
     * @param timestamp
     * @returns response
     * */
    public onSuccess(response: any, timeStart): Observable<any> {
        if (!_.isNil(response) && !_.isNil(response.url) && !_.isNil(response._body)) {
            if (_.includes(response.url, 'onLoad') || _.includes(response.url, 'bulkUpload')) {
                const errMsg = '[{"errorCode":"GENERIC_ERROR","message":"GENERIC_ERROR"}]';
                if (_.includes(response._body, errMsg)) {
                    response._body = response._body.replace(errMsg, '');
                }
            }
        }

        const timeEnd: number = performance.now();
        const newTime = timeEnd - timeStart;
        this.hideLoader();
        return response;
    }
    public downloadonSuccess(response: any) {
        this.hideLoader();
        return response;
    }
    /**
     * Global http error handler.
     *
     * @param error
     * @param source
     * @returns {ErrorObservable}
     */
    public onCatch(response: any, source: Observable<any>): Observable<any> {

        switch (response.status) {
            case 400:
                this.handleBadRequest(response);
                break;

            case 401:
                this.handleUnauthorized(response);
                break;

            case 403:
                this.handleForbidden(response);
                break;

            case 404:
                this.handleNotFound(response);
                this.hideLoader();
                break;

            case 500:
                this.handleServerError(response);
                this.hideLoader();
                break;

            default:
                this.handleDefaultError(response);
                break;
        }
        this.hideLoader();
        return throwError(()=>response.json());
    }

    /**
     * Shows notification errors when server response status is 400
     *
     * @param error
     */
    private handleBadRequest(responseBody: any): void {
        if (responseBody._body) {
            try {
                const bodyParsed = responseBody.json();
                this.handleErrorMessages(bodyParsed);
            } catch (error) {
                this.handleServerError(error);
            }
        } else {
            this.handleServerError(responseBody.error);
        }
    }

    /**
     * Shows notification errors when server response status is 401 and redirects user to login page
     *
     * @param responseBody
     */
    private handleUnauthorized(response: any): void {
        this.logger.logException('TCCP-http', 'handleUnauthorized', '401 Error', response, response.error);

    }

    /**
     * Shows notification errors when server response status is 403
     */
    private handleForbidden(response: any): void {
        this.logger.logException('TCCP-http', 'handleForbidden', '403 Error', response, response.error);
        this.router.navigate(['/login']);
    }

    /**
     * Shows notification errors when server response status is 404
     *
     * @param responseBody
     */
    private handleNotFound(response: any): void {
        this.logger.logException('TCCP-http', 'handleNotFound', '404 Error', response, response.error);
        this.router.navigate(['/404']);
    }

    /**
     * Shows notification errors when server response status is 500
     */
    private handleServerError(response: any): void {
        this.logger.logException('TCCP-http', 'handleServerError', 'Server Error', response, response.error);
        this.router.navigate(['/500']);
    }

    /**
     * Parses server response and shows notification errors with translated messages
     *
     * @param response
     */
    private handleErrorMessages(response: any): void {
        this.logger.logException('TCCP-http', 'handleErrorMessages', '400 Error', response, response.error());
        this.router.navigate(['/400']);
    }


    /**
     * Shows notification errors when there is a default error
     *
     * @param response
     */
    private handleDefaultError(response: any): void {
        this.logger.logException('TCCP-http', 'handleDefaultError', 'Default Error', response, response.error);
    }

    private showLoader(): void {
        this.loaderService.show();
    }

    private hideLoader(): void {
        this.loaderService.hide();
    }
}
